import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import * as ContactOverViewActions from '../overview/store/contact-detail-overview-actions';
import * as ContactPermissionActions from '../permissions/store/contact-detail-permissions-actions';
import { Api } from '../../../../../api';
import { AppState } from '../../../../../store/models/app-state';
import { ErrorMessage } from '../../../../../common/models/enums/error-message';
import { ReduxDispatch } from '../../../../../store/use-thunk-dispatch';
import { SaveResult } from '../../../../../common/models/save-result';
import { contactDetailIsValidSelector } from './contact-detail-global-selectors';
import { PALO_MASTER_DATA_LABELS } from '../../../../../common/components/palo/palo-master-data-labels';
import { paloMissingFieldsMessage } from '../../../../../common/components/palo/palo-missing-fields-message';

export const CONTACT_DETAIL_RESET = 'CONTACT_DETAIL_RESET';
export const CONTACT_DETAIL_SAVE = 'CONTACT_DETAIL_SAVE';
export const CONTACT_DETAIL_DELETE = 'CONTACT_DETAIL_DELETE';
export const CONTACT_DETAIL_DELETE_SUCCESS = 'CONTACT_DETAIL_DELETE_SUCCESS';
export const CONTACT_DETAIL_DELETE_FAILED = 'CONTACT_DETAIL_DELETE_FAILED';
export const CONTACT_DETAIL_LOCK = 'CONTACT_DETAIL_LOCK';
export const CONTACT_DETAIL_LOCK_SUCCESS = 'CONTACT_DETAIL_LOCK_SUCCESS';
export const CONTACT_DETAIL_LOCK_FAILED = 'CONTACT_DETAIL_LOCK_FAILED';
export const CONTACT_DETAIL_UNLOCK = 'CONTACT_DETAIL_UNLOCK';
export const CONTACT_DETAIL_CONDITION_ERROR = 'CONTACT_DETAIL_CONDITION_ERROR';
export const CONTACT_DETAIL_CONDITION_ERROR_RESET = 'CONTACT_DETAIL_CONDITION_ERROR_RESET';

export const resetContactDetails = () => ({ type: CONTACT_DETAIL_RESET });
export const resetContactDetailsConditionError = () => ({ type: CONTACT_DETAIL_CONDITION_ERROR_RESET });

export const handleLockContactSuccess = () => ({ type: CONTACT_DETAIL_LOCK_SUCCESS });

const hasContactAssignedError = (title: string | undefined) => {
    return (
        title === ErrorMessage.ContactIsAssignedAsMainContact ||
        title === ErrorMessage.ContactIsAssignedToBonusCondition
    );
};

export const deleteContact = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_DELETE });

        const id = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!id) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.delete(id);

        if (response.success) {
            dispatch({ type: CONTACT_DETAIL_DELETE_SUCCESS });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.contactDeleted')));
        } else if (hasContactAssignedError(response.error?.title)) {
            dispatch({
                type: CONTACT_DETAIL_CONDITION_ERROR,
                context: response.error?.context,
                errorType: response.error?.title,
            });
            return Promise.reject();
        } else {
            dispatch({ type: CONTACT_DETAIL_DELETE_FAILED });
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
            return Promise.reject();
        }
    };
};

export const lockContact = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_LOCK });

        const id = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!id) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.lock(id);

        if (response.success) {
            dispatch(handleLockContactSuccess());
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.contactLocked')));
        } else if (hasContactAssignedError(response.error?.title)) {
            dispatch({ type: CONTACT_DETAIL_CONDITION_ERROR, context: response.error?.context });
        } else {
            dispatch({ type: CONTACT_DETAIL_LOCK_FAILED });
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const unlockContact = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_UNLOCK });

        const id = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!id) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.unlock(id);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.contactUnlocked')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const saveContactDetails = () => {
    return async (dispatch: ReduxDispatch, getState: () => AppState) => {
        dispatch({ type: CONTACT_DETAIL_SAVE });

        const state = getState();
        const isAllValid = contactDetailIsValidSelector(state);
        const contactState = state.contactsAndOrganizations.contacts.contactDetail;

        if (!isAllValid) {
            // Which fields, and on which tab — the tab holding them need not
            // be the one being looked at, so "check the form" sent people
            // looking (#2212, R-28).
            const missing = paloMissingFieldsMessage([
                {
                    tab: 'app.contactsAndOrganizations.detail.tab.details',
                    errors: contactState.overview.overviewErrors,
                    // The person's own address, not one of the extra ones.
                    labels: PALO_MASTER_DATA_LABELS,
                },
                {
                    tab: 'app.contactsAndOrganizations.detail.tab.permissions',
                    errors: contactState.permissions.errors,
                },
            ]);

            dispatch(
                Snackbar.enqueue(
                    missing
                        ? PoolNotification.error('app.snackbar.formErrorsNamed', { fields: missing })
                        : PoolNotification.error('app.snackbar.formErrors')
                )
            );
            return;
        }

        const promises: Promise<SaveResult>[] = [];

        if (contactState.overview.isDirty) {
            promises.push(dispatch(ContactOverViewActions.saveContactDetail()));
        }
        if (contactState.permissions.isDirty) {
            promises.push(dispatch(ContactPermissionActions.savePermissions()));
        }
        // dispatch other save actions here

        // Nothing dirty means nothing was written. On a record that exists that
        // is simply a no-op, but on a new one it means the form was never
        // filled in — and "Gespeichert" would claim a record that does not
        // exist (#2212, seen on an untouched new person).
        if (0 === promises.length) {
            // Read again: the mask validates just before it asks to save, and
            // by now the fields it marked have reached the store.
            const named = paloMissingFieldsMessage([
                {
                    tab: 'app.contactsAndOrganizations.detail.tab.details',
                    errors: getState().contactsAndOrganizations.contacts.contactDetail.overview.overviewErrors,
                    labels: PALO_MASTER_DATA_LABELS,
                },
                {
                    tab: 'app.contactsAndOrganizations.detail.tab.permissions',
                    errors: getState().contactsAndOrganizations.contacts.contactDetail.permissions.errors,
                },
            ]);

            dispatch(
                Snackbar.enqueue(
                    named
                        ? PoolNotification.error('app.snackbar.formErrorsNamed', { fields: named })
                        : PoolNotification.error('app.snackbar.formErrors')
                )
            );

            return;
        }

        const results = await Promise.all(promises);
        if (results.every(result => result === SaveResult.Success)) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.saved')));
        } else if (results.some(result => result === SaveResult.FormErrors)) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.formErrors')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
